/* Answering somebody's poll, without an account.
 *
 * This is the one screen in the app that a stranger sees, and the only one
 * reachable without signing in. That is deliberate and it is the whole reason
 * a poll works at all: a sign-up wall is how a poll gets three answers out of
 * nine.
 *
 * So it asks for exactly one thing — which of these times work — and offers
 * nothing else. No sign-up prompt in the way, no account required to change an
 * answer later, and nothing about the organiser's calendar beyond the times
 * they chose to put in front of you.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { get, post, errorMessage } from '../api';
import { friendlyDate } from '../ui/time';

type Answer = 'yes' | 'maybe' | 'no';

interface Slot {
  id: string; date: string; start: string; end: string;
  yes: number; maybe: number; no: number;
  mine: Answer | null;
}

interface Poll {
  title: string; minutes: number; notes: string | null;
  decidedSlot: string | null;
  slots: Slot[];
  you: { id: string; name: string };
  organiser: string;
  answered: number; invited: number;
}

const CHOICES: { value: Answer; label: string }[] = [
  { value: 'yes', label: 'Works' },
  { value: 'maybe', label: 'At a push' },
  { value: 'no', label: 'No' }
];

export default function AnswerScreen() {
  const { token = '' } = useParams();
  const qc = useQueryClient();
  const [answers, setAnswers] = useState<Record<string, Answer>>({});
  const [saved, setSaved] = useState(false);

  const poll = useQuery({
    queryKey: ['answer', token],
    queryFn: async () => {
      const r = await get<{ meeting: Poll }>(`/api/meetings/answer/${token}`);
      if (!r.ok) throw new Error(errorMessage(r, 'That link does not open anything.'));
      return r.meeting;
    }
  });

  const send = useMutation({
    mutationFn: async () => {
      const r = await post(`/api/meetings/answer/${token}`, { answers });
      if (!r.ok) throw new Error(errorMessage(r, 'Could not save your answer.'));
      return r;
    },
    onSuccess: () => {
      setSaved(true);
      void qc.invalidateQueries({ queryKey: ['answer', token] });
    }
  });

  const m = poll.data;
  /* What is on screen: what they have just picked, falling back to whatever
     they said last time. Changing your mind is the ordinary case. */
  const current = (s: Slot) => answers[s.id] ?? s.mine ?? null;

  return (
    <div className="screen answer-screen">
      <header className="topbar">
        <div>
          <h1>{m ? m.title : 'A time to meet'}</h1>
          <p className="muted">
            {m ? `${m.organiser} is trying to find ${m.minutes} minutes. You are ${m.you.name}.`
              : 'Opening…'}
          </p>
        </div>
      </header>

      {poll.isError && (
        <div className="notice notice-bad" role="alert">
          <p><strong>That link does not open anything.</strong></p>
          <p className="muted small">
            It may have been withdrawn, or the meeting may already be settled. Ask
            whoever sent it.
          </p>
        </div>
      )}

      {m?.notes && <p className="muted">{m.notes}</p>}

      {m?.decidedSlot && (
        <div className="notice">
          <p><strong>This one is settled.</strong></p>
          <p className="muted small">
            {(() => {
              const s = m.slots.find(x => x.id === m.decidedSlot);
              return s ? `${friendlyDate(s.date)}, ${s.start}–${s.end}.` : '';
            })()}
          </p>
        </div>
      )}

      {m && !m.decidedSlot && (
        <section className="panel">
          <div className="panel-head">
            <h2>Which of these work?</h2>
            <span className="muted small">{m.answered} of {m.invited} have answered</span>
          </div>

          <ul className="answer-list">
            {m.slots.map((s, i) => {
              /* The date only when it changes. Three options on one Wednesday
                 printed "Wednesday, September 9" three times and buried the
                 one thing that differed — which is the time. */
              const sameAsAbove = i > 0 && m.slots[i - 1].date === s.date;
              return (
              <li key={s.id}>
                <div>
                  <strong className="answer-time">{s.start}–{s.end}</strong>
                  <span className="muted small">
                    {sameAsAbove ? ' · same day' : ` · ${friendlyDate(s.date)}`}
                  </span>
                </div>
                <div className="inline" role="group"
                     aria-label={`${friendlyDate(s.date)} at ${s.start}`}>
                  {CHOICES.map(c => (
                    <label key={c.value}
                           className={`chip chip-pick${current(s) === c.value ? ' is-on' : ''}`}>
                      <input type="radio" name={s.id} value={c.value}
                             checked={current(s) === c.value}
                             onChange={() => {
                               setAnswers(a => ({ ...a, [s.id]: c.value }));
                               setSaved(false);
                             }} />
                      {c.label}
                    </label>
                  ))}
                </div>
              </li>
              );
            })}
          </ul>

          <div className="inline wrap">
            <button className="btn btn-primary btn-sm"
                    disabled={send.isPending || !Object.keys(answers).length}
                    onClick={() => send.mutate()}>
              {send.isPending ? 'Sending…' : 'Send my answer'}
            </button>
            {saved && <span className="muted small">
              Sent. You can change it from this same link whenever you like.
            </span>}
            {send.isError && (
              <span className="auth-err" role="alert">{(send.error as Error).message}</span>
            )}
          </div>
        </section>
      )}

      <p className="muted small">
        Answering does not need an account, and nothing about your own calendar is
        read or stored by this page.
      </p>
    </div>
  );
}
